import { BUREAUCRAT_BASE_URI } from './config';
import { getConsoleLogger, getLogger } from './logger';
import BureaucratError from './BureaucratError';
import NetworkError from './NetworkError';
import TimeoutError from './TimeoutError';

let IsLoggedIn;
let checkingLoggedInPromise;
const logger = getLogger('Bureaucrat');
const consoleLogger = getConsoleLogger('Bureaucrat');

async function fetchIt(
  path,
  options = { cache: 'no-cache', credentials: 'include' },
  timeoutMillis = 60000,
) {
  const url = `${BUREAUCRAT_BASE_URI}/${path}`;
  const method = options.method || 'GET';

  const timeout = setTimeout(() => {
    throw new TimeoutError(url);
  }, timeoutMillis);

  if (path !== 'logs') {
    // logging our post to logs will result in infinite loop
    logger.debug(`${method} ${url}`);
  }

  try {
    const response = await fetch(url, options);
    clearTimeout(timeout);
    return response;
  } catch (reason) {
    consoleLogger.debug(reason);
    throw new NetworkError(url, reason.message);
  }
}

function verifyOkResponse(response) {
  logger.debug(
    `response: ${response.url} ${response.status} ${response.statusText}`,
  );

  if (response.ok) {
    return response.json();
  }

  throw new BureaucratError(response.url, response.status, response.statusText);
}

function checkForErrors(response) {
  if (!response.url.endsWith('logs')) {
    logger.debug(
      `response: ${response.url} ${response.status} ${response.statusText}`,
    );
  }

  if (response.ok) {
    return response.json().catch(() => {
      /** do nothing, no json in the response * */
    });
  }

  const error = new BureaucratError(
    response.url,
    response.status,
    response.statusText,
  );

  return response
    .json()
    .catch(() => {
      // no json body
      throw error;
    })
    .then((body) => {
      error.body = body;
      throw error;
    });
}

const bureaucrat = {
  whatNext: () => fetchIt('whatNext').then(verifyOkResponse),

  whatNextSkype: () => fetchIt('whatNextSkype').then(verifyOkResponse),

  getBillingProfile: () => fetchIt('billing-profile').then(verifyOkResponse),

  updateBillingProfile: (
    companyName,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    firstName,
    lastName,
    email,
    phoneNumber,
  ) => {
    const data = new URLSearchParams();
    data.append('companyName', companyName);
    data.append('addressLine1', addressLine1);
    data.append('addressLine2', addressLine2);
    data.append('city', city);
    data.append('state', state);
    data.append('postalCode', postalCode);
    data.append('firstName', firstName);
    data.append('lastName', lastName);
    data.append('email', email);
    data.append('phoneNumber', phoneNumber);

    return fetchIt('billing-profile', {
      credentials: 'include',
      method: 'POST',
      body: data,
    }).then(checkForErrors);
  },

  acceptAgreement: () =>
    fetchIt('accept-agreement', {
      credentials: 'include',
      method: 'POST',
    }).then(checkForErrors),

  activateLicense: (activationKey) => {
    const data = new URLSearchParams();
    data.append('license', activationKey);

    return fetchIt('activate-license', {
      credentials: 'include',
      method: 'POST',
      body: data,
    }).then(checkForErrors);
  },

  confirmHybrid: (stratusEmail) => {
    const data = new URLSearchParams();
    data.append('stratusEmail', stratusEmail);

    return fetchIt('hybridConfirm', {
      credentials: 'include',
      method: 'POST',
      body: data,
    }).then(checkForErrors);
  },

  getHybridOptions: () => fetchIt('hybrid').then(verifyOkResponse),

  updateHybridOptions: (
    encodedCertificate,
    certificateKey,
    skypeUser,
    skypeDomain,
    skypeAddress,
    skypePort,
    stratusEmail,
  ) => {
    const data = new URLSearchParams();
    data.append('encodedCertificate', encodedCertificate);
    data.append('certificateKey', certificateKey);
    data.append('skypeUser', skypeUser);
    data.append('skypeDomain', skypeDomain);
    data.append('skypeAddress', skypeAddress);
    data.append('skypePort', skypePort);
    data.append('stratusEmail', stratusEmail);

    return fetchIt('hybrid', {
      credentials: 'include',
      method: 'POST',
      body: data,
    }).then(checkForErrors);
  },

  checkEdge(skypeDomain) {
    return fetchIt(`checkedge?domain=${skypeDomain}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(checkForErrors);
  },

  sendLogs: (logMessage) => {
    const log = {
      service: 'diplomat',
      message: JSON.stringify(logMessage),
    };

    return fetchIt('logs', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      body: JSON.stringify(log),
    }).then(checkForErrors);
  },

  updateTenant: (tenant) =>
    fetchIt(`tenants/${tenant.id}`, {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },

      body: JSON.stringify(tenant),
    }).then(checkForErrors),

  getTenantProfile: () => fetchIt(`tenant-profile/`).then(verifyOkResponse),

  getTenantPrompts: () =>
    fetchIt(`teams-gw-prompts/tenant`).then(verifyOkResponse),

  updateTenantPrompts: (prompts) =>
    fetchIt(`teams-gw-prompts/tenant`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      body: JSON.stringify(prompts),
    }).then(verifyOkResponse),

  getDefaultPrompts: () =>
    fetchIt(`teams-gw-prompts/default`).then(verifyOkResponse),

  getTask: (taskId) => fetchIt(`tasks/${taskId}`).then(verifyOkResponse),

  isLoggedIn: async () => {
    if (IsLoggedIn === undefined) {
      if (checkingLoggedInPromise === undefined) {
        checkingLoggedInPromise = bureaucrat.sendLogs('');
        const data = await checkingLoggedInPromise;
        IsLoggedIn = data === undefined;
      } else {
        await checkingLoggedInPromise;
      }
    }
    return IsLoggedIn;
  },

  logoutUrl: `${BUREAUCRAT_BASE_URI}/logout`,

  requestTrial: (preferredReseller, emailOptIn) => {
    const data = new URLSearchParams();
    data.append('preferredReseller', preferredReseller);
    data.append('emailOptIn', emailOptIn);

    return fetchIt('request-trial', {
      credentials: 'include',
      method: 'POST',
      body: data,
    }).then(checkForErrors);
  },
};

export default bureaucrat;
